import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CasinoRoundedIcon from "@mui/icons-material/CasinoRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

function stripHtml(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * List of bank questions picked for a quiz (create / edit / AI draft).
 * Private-bank items can be edited; any item can be removed from the quiz pin set.
 */
export default function QuizQuestionPreviewPanel({
  questions = [],
  requested = 0,
  poolTotal = 0,
  picked = 0,
  loading = false,
  error = "",
  onRandomize,
  onReload,
  onAddQuestions,
  onEditQuestion,
  onRemoveQuestion,
  dense = false,
  title = "Questions for this quiz",
}) {
  const theme = useTheme();
  const darkMode = theme.palette.mode === "dark";
  const canManage = Boolean(onEditQuestion || onRemoveQuestion);

  return (
    <Box
      sx={{
        mt: dense ? 1 : 2,
        p: dense ? 1.25 : 1.75,
        borderRadius: 2.5,
        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
        bgcolor: darkMode
          ? alpha(theme.palette.info.main, 0.08)
          : alpha(theme.palette.info.main, 0.05),
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        mb={1.25}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <VisibilityOutlinedIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {loading
                ? "Loading questions…"
                : `Showing ${picked || questions.length} of ${poolTotal || "—"} available · asked for ${requested || "—"}`}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {onAddQuestions ? (
            <Button
              size="small"
              variant="outlined"
              color="info"
              disabled={loading}
              startIcon={<AddRoundedIcon />}
              onClick={onAddQuestions}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: darkMode ? "#E2E8F0" : theme.palette.info.dark,
                borderColor: darkMode
                  ? alpha("#fff", 0.35)
                  : alpha(theme.palette.info.main, 0.55),
                bgcolor: darkMode ? alpha("#fff", 0.04) : "#fff",
                "&:hover": {
                  bgcolor: darkMode
                    ? alpha("#fff", 0.1)
                    : alpha(theme.palette.info.main, 0.08),
                  borderColor: theme.palette.info.main,
                },
                "&.Mui-disabled": {
                  color: darkMode ? alpha("#fff", 0.35) : alpha(theme.palette.text.primary, 0.35),
                  borderColor: darkMode
                    ? alpha("#fff", 0.15)
                    : alpha(theme.palette.text.primary, 0.15),
                },
              }}
            >
              Add questions
            </Button>
          ) : null}
          {onReload ? (
            <Button
              size="small"
              variant="outlined"
              disabled={loading}
              startIcon={<RefreshRoundedIcon />}
              onClick={onReload}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: darkMode ? "#E2E8F0" : theme.palette.text.primary,
                borderColor: darkMode
                  ? alpha("#fff", 0.35)
                  : alpha(theme.palette.text.primary, 0.28),
                bgcolor: darkMode ? alpha("#fff", 0.04) : "#fff",
                "&:hover": {
                  bgcolor: darkMode
                    ? alpha("#fff", 0.1)
                    : alpha(theme.palette.text.primary, 0.04),
                  borderColor: darkMode
                    ? alpha("#fff", 0.5)
                    : alpha(theme.palette.text.primary, 0.45),
                },
                "&.Mui-disabled": {
                  color: darkMode ? alpha("#fff", 0.35) : alpha(theme.palette.text.primary, 0.35),
                  borderColor: darkMode
                    ? alpha("#fff", 0.15)
                    : alpha(theme.palette.text.primary, 0.15),
                },
              }}
            >
              Reload
            </Button>
          ) : null}
          {onRandomize ? (
            <Button
              size="small"
              variant="contained"
              color="info"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <CasinoRoundedIcon />}
              onClick={onRandomize}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#fff",
                bgcolor: theme.palette.info.main,
                boxShadow: darkMode ? "none" : "0 1px 3px rgba(15,23,42,0.12)",
                "&:hover": {
                  bgcolor: theme.palette.info.dark,
                  color: "#fff",
                },
                "&.Mui-disabled": {
                  color: alpha("#fff", 0.7),
                },
              }}
            >
              Randomize
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {error ? (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      ) : null}

      {loading && !questions.length ? (
        <Box py={3} display="flex" justifyContent="center">
          <CircularProgress size={22} />
        </Box>
      ) : null}

      {!loading && !error && !questions.length ? (
        <Typography variant="body2" color="text.secondary">
          No questions matched these rules yet. Adjust the bank / topic / count, then randomize again.
        </Typography>
      ) : null}

      <Stack spacing={1.1} divider={<Divider flexItem sx={{ opacity: 0.5 }} />}>
        {questions.map((question, index) => {
          const body = stripHtml(question.question_content);
          const canEdit = Boolean(onEditQuestion) && question.editable !== false && question.bank === "private";
          const canRemove = Boolean(onRemoveQuestion);
          return (
            <Box key={`${question.bank}-${question.id}-${index}`}>
              <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      Q{index + 1}
                    </Typography>
                    <Chip
                      size="small"
                      label={question.bank === "microlab" ? "Microlab bank" : "Private bank"}
                      sx={{ height: 22, fontWeight: 600 }}
                    />
                    {question.difficulty ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={String(question.difficulty)}
                        sx={{ height: 22, textTransform: "capitalize" }}
                      />
                    ) : null}
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {body || "Untitled question"}
                  </Typography>
                  <Stack spacing={0.35}>
                    {(question.answers || []).map((answer) => (
                      <Typography
                        key={`${question.id}-${answer.key}`}
                        variant="caption"
                        sx={{
                          color: answer.is_correct
                            ? theme.palette.success.main
                            : "text.secondary",
                          fontWeight: answer.is_correct ? 700 : 500,
                        }}
                      >
                        {String(answer.key || "").toUpperCase()}. {stripHtml(answer.text)}
                        {answer.is_correct ? " ✓" : ""}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
                {canManage ? (
                  <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                    {canEdit ? (
                      <Tooltip title="Edit private bank wording (shared bank row)">
                        <IconButton
                          size="small"
                          aria-label={`Edit question ${index + 1}`}
                          onClick={() => onEditQuestion(question)}
                          sx={{ color: theme.palette.info.main }}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {canRemove ? (
                      <Tooltip title="Remove from this quiz only (bank original kept)">
                        <IconButton
                          size="small"
                          aria-label={`Remove question ${index + 1}`}
                          onClick={() => onRemoveQuestion(question, index)}
                          sx={{ color: theme.palette.error.main }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

QuizQuestionPreviewPanel.propTypes = {
  questions: PropTypes.array,
  requested: PropTypes.number,
  poolTotal: PropTypes.number,
  picked: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRandomize: PropTypes.func,
  onReload: PropTypes.func,
  onAddQuestions: PropTypes.func,
  onEditQuestion: PropTypes.func,
  onRemoveQuestion: PropTypes.func,
  dense: PropTypes.bool,
  title: PropTypes.string,
};
